export const DEFAULT_FIRMWARE_FILTERS = {
  organizationId: '',
  /** `all` = Telegram-style matrix (all four risks). */
  riskNumber: 'all',
  historyRange: 'month',
  riskLevel: '',
  hideTest: true,
};

export const FIRMWARE_RISK_NUMBER_OPTIONS = [
  { value: 'all', label: 'Всё' },
  { value: '1', label: 'Риск №1 · Нет связи' },
  { value: '2', label: 'Риск №2 · Нет показаний' },
  { value: '3', label: 'Риск №3 · Слабый сигнал' },
  { value: '4', label: 'Риск №4 · Батарея' },
];

const UNKNOWN_LABELS = new Set(['—', '-', '(без версии)', 'без версии', 'unknown', 'null', 'undefined']);

export function isUnknownFirmwareVersion(value) {
  if (value == null) {
    return true;
  }
  const text = String(value).trim();
  return !text || UNKNOWN_LABELS.has(text.toLowerCase()) || UNKNOWN_LABELS.has(text);
}

export function formatFirmwareVersionLabel(value) {
  return isUnknownFirmwareVersion(value) ? '—' : String(value).trim();
}

/**
 * Parse "2.0.23" / "v1.6.19" into numeric segments [2, 0, 23].
 * Compare segment-by-segment as numbers (not strings): 1.6.15 > 1.6.6.
 */
export function parseFirmwareVersionParts(value) {
  const text = String(value || '')
    .trim()
    .replace(/^v/i, '');
  const parts = text.match(/\d+/g);
  return parts ? parts.map((part) => Number.parseInt(part, 10)) : [];
}

/** Newest first (desc). Unknown / dash always last. */
export function compareFirmwareVersionsNewestFirst(left, right) {
  const leftUnknown = isUnknownFirmwareVersion(left);
  const rightUnknown = isUnknownFirmwareVersion(right);

  if (leftUnknown && rightUnknown) {
    return 0;
  }
  if (leftUnknown) {
    return 1;
  }
  if (rightUnknown) {
    return -1;
  }

  const leftParts = parseFirmwareVersionParts(left);
  const rightParts = parseFirmwareVersionParts(right);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftPart = leftParts[index] ?? 0;
    const rightPart = rightParts[index] ?? 0;
    if (rightPart !== leftPart) {
      return rightPart - leftPart;
    }
  }

  return String(right).localeCompare(String(left), 'en', { numeric: true, sensitivity: 'base' });
}

export function sortFirmwareVersionRows(rows) {
  return [...(rows || [])].sort((left, right) =>
    compareFirmwareVersionsNewestFirst(left.firmware_version, right.firmware_version),
  );
}

export function parseFirmwareQuery(searchParams) {
  const riskNumberRaw = searchParams.get('riskNumber');
  return {
    organizationId: searchParams.get('organizationId') || DEFAULT_FIRMWARE_FILTERS.organizationId,
    riskNumber: ['all', '1', '2', '3', '4'].includes(riskNumberRaw)
      ? riskNumberRaw
      : DEFAULT_FIRMWARE_FILTERS.riskNumber,
    historyRange: ['month', 'year', 'all'].includes(searchParams.get('historyRange'))
      ? searchParams.get('historyRange')
      : DEFAULT_FIRMWARE_FILTERS.historyRange,
    riskLevel: searchParams.get('riskLevel') || DEFAULT_FIRMWARE_FILTERS.riskLevel,
    hideTest: searchParams.get('hideTest') !== '0' && searchParams.get('hideTest') !== 'false',
  };
}

export function buildFirmwareSearchParams(filters) {
  const params = new URLSearchParams();

  if (filters.organizationId) {
    params.set('organizationId', String(filters.organizationId));
  }
  if (filters.riskNumber && filters.riskNumber !== 'all') {
    params.set('riskNumber', String(filters.riskNumber));
  }
  if (filters.historyRange && filters.historyRange !== 'month') {
    params.set('historyRange', String(filters.historyRange));
  }

  if (filters.riskLevel) {
    params.set('riskLevel', filters.riskLevel);
  }

  if (filters.hideTest === false) {
    params.set('hideTest', '0');
  }

  return params;
}

export function buildFirmwareStatsQuery(filters) {
  const params = new URLSearchParams();

  if (filters.organizationId) {
    params.set('organization_id', String(filters.organizationId));
  }
  params.set('risk_number', String(filters.riskNumber || '1'));

  if (filters.riskLevel) {
    params.set('risk_level', filters.riskLevel);
  }

  if (filters.hideTest === false) {
    params.set('hide_test', '0');
  }

  return params.toString();
}

/** Telegram-style all-risks matrix (same source as the TG photo). */
export function buildVersionsReportQuery(filters) {
  const params = new URLSearchParams();
  if (filters.organizationId) {
    params.set('organization_id', String(filters.organizationId));
  }
  if (filters.hideTest === false) {
    params.set('hide_test', '0');
  }
  return params.toString();
}

export function isAllRisksView(riskNumber) {
  return !riskNumber || riskNumber === 'all';
}

/** Apply version/connection filters and open Dashboard (embedded risks section). */
export function buildDashboardFilterPatch({
  organizationId,
  firmwareVersion,
  connectionType,
  riskNumber = '1',
}) {
  return {
    riskTypes: String(riskNumber),
    showAll: true,
    hideTest: true,
    organizationId: organizationId ? String(organizationId) : '',
    firmwareVersion:
      firmwareVersion && firmwareVersion !== '—' && firmwareVersion !== '(без версии)'
        ? firmwareVersion
        : '',
    connectionType: connectionType || '',
    riskLevel: '',
  };
}
